import { useState } from "react";
import { faStar as filledStar } from "@fortawesome/pro-solid-svg-icons";
import { faStar as emptyStar } from "@fortawesome/pro-light-svg-icons";
import cx from "classnames";

import { rangeArray } from "common/utils/numbers";
import { ReviewRating } from "common/types";
import Icon from "common/components/Icon";

import ErrorMessage from "client/common/components/errors/ErrorMessage";

import styles from "../styles/ReviewStarsInput.module.less";

type RenderReviewStarProps = {
  star: React.ReactNode;
  onChange: () => void;
  "data-rating": ReviewRating;
  className: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

type ReviewStarProps = {
  rating: ReviewRating;
  value?: ReviewRating;
  onChange: (value: ReviewRating) => void;
  hover?: ReviewRating;
  onSetHover: (value?: ReviewRating) => void;
  renderReviewStar: (props: RenderReviewStarProps) => React.ReactElement;
};

const ReviewStar = ({
  rating,
  value,
  onChange,
  hover,
  onSetHover,
  renderReviewStar,
}: ReviewStarProps) => {
  const isFilledForInput = value && rating <= value;
  const isFilledForHover = hover && rating <= hover;
  const isFilled = hover ? isFilledForHover : isFilledForInput;
  const star = (
    <Icon
      icon={isFilled ? filledStar : emptyStar}
      className={`${styles.star} ${hover ? styles.hover : ""}`}
    />
  );

  return renderReviewStar({
    // Handle these directly
    star,
    onChange: () => onChange(rating),
    // Pass through to the DOM
    "data-rating": rating,
    className: styles.starButton,
    onMouseEnter: () => onSetHover(rating),
    onMouseLeave: () => onSetHover(undefined),
  });
};

const defaultRenderReviewStar = ({ star, onChange, ...other }: RenderReviewStarProps) => {
  return (
    <button type="button" onClick={onChange} {...other}>
      {star}
    </button>
  );
};

const noop = () => null;

type ReviewStarsInputProps = {
  value?: ReviewRating;
  size?: "s" | "m";
  isStatic?: boolean;
  onChange?: (value: ReviewRating) => void;
  errors?: string[];
  renderReviewStar?: (props: RenderReviewStarProps) => React.ReactElement;
};

const ReviewStarsInput = ({
  value,
  isStatic = false,
  size = "m",
  onChange = noop,
  errors,
  renderReviewStar = defaultRenderReviewStar,
}: ReviewStarsInputProps) => {
  const [hover, setHover] = useState<ReviewRating | undefined>(undefined);

  return (
    <div>
      <div className={cx(styles.stars, styles[size], isStatic && styles.static)}>
        {(rangeArray(1, 6) as ReviewRating[]).map((rating) => (
          <ReviewStar
            key={rating}
            rating={rating}
            value={value}
            onChange={onChange}
            hover={hover}
            onSetHover={setHover}
            renderReviewStar={renderReviewStar}
          />
        ))}
      </div>
      <div>{errors?.map((error) => <ErrorMessage key={error}>{error}</ErrorMessage>)}</div>
    </div>
  );
};

export default ReviewStarsInput;
